import {
	calculateHeaterClearance,
	type HeaterClearanceResult,
} from "../../domain/heater/calculateHeaterClearance";
import { resolveHeaterClearanceLayout } from "../../engines/interiorLayoutResolver";
import type { BuildInput } from "../../engines/types";

export interface ClearanceEnvelopeBox {
	status: HeaterClearanceResult["status"];
	centerM: [number, number, number];
	sizeM: [number, number, number];
}

/**
 * The box `HeaterClearanceOverlay` draws — its centre and its extents, in metres.
 *
 * This IS the drawn geometry: the overlay's `<group>` sits at `centerM` and its
 * `<boxGeometry>` is built from `sizeM`, so a test that measures this against the
 * heater meshes `buildSauna3D` emits is measuring the rendered zone, not a
 * stand-in for it. The overlay drifting off the aggregat is invisible in the DOM
 * and invisible in the compliance verdict — comparing these numbers with the
 * model's is the only way to catch it.
 *
 * It lives in its own module rather than beside the component because a file that
 * exports both a component and a helper breaks Fast Refresh
 * (`react-refresh/only-export-components`). The test imports it from here.
 *
 * The envelope is resolved through `resolveHeaterClearanceLayout`, which is the
 * same call the enrichment chain makes. Calling `deriveHeaterClearanceLayout`
 * directly draws the zone around the aggregat's GENERATED position — which
 * stopped being where the model puts it once the renderer began following
 * `layout.heater` (issue 131). A safety envelope drawn around empty floor is
 * worse than none, and the verdict text being correct hides it.
 */
export function resolveClearanceEnvelope(
	buildInput: BuildInput,
): ClearanceEnvelopeBox | null {
	const layout = resolveHeaterClearanceLayout(buildInput);
	if (!layout) return null;
	const result = calculateHeaterClearance(
		{
			heaterType: buildInput.thermalInputs.heaterType,
			heaterModelId: buildInput.thermalInputs.heaterModelId,
			...layout,
		},
		buildInput.overrides,
	);
	const { envelope } = result;
	return {
		status: result.status,
		centerM: [
			(envelope.minX + envelope.maxX) / 2,
			(envelope.minY + envelope.maxY) / 2,
			(envelope.minZ + envelope.maxZ) / 2,
		],
		sizeM: [
			envelope.maxX - envelope.minX,
			envelope.maxY - envelope.minY,
			envelope.maxZ - envelope.minZ,
		],
	};
}
